import { useCallback, useEffect, useMemo, useState } from 'react'
import { EmptyState } from '../components/EmptyState'
import { InlineNotice } from '../components/InlineNotice'
import { LoadingBlock } from '../components/LoadingBlock'
import { Modal } from '../components/Modal'
import { StatusBadge } from '../components/StatusBadge'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { clientDisplayName, formatDate, formatMoney } from '../lib/format'
import { supabase } from '../lib/supabase'
import { consumePendingAction } from '../lib/pendingAction'
import type { ClientRow, ClientType, SimpleSource } from '../lib/types'

type ClientForm = {
  client_type: ClientType
  first_name: string
  last_name: string
  company_name: string
  email: string
  phone: string
  instagram_handle: string
  preferred_contact: string
  source_id: string
  notes: string
  marketing_email: boolean
  marketing_sms: boolean
  marketing_phone: boolean
  active: boolean
}

const emptyForm: ClientForm = {
  client_type: 'individual',
  first_name: '',
  last_name: '',
  company_name: '',
  email: '',
  phone: '',
  instagram_handle: '',
  preferred_contact: 'instagram',
  source_id: '',
  notes: '',
  marketing_email: false,
  marketing_sms: false,
  marketing_phone: false,
  active: true
}

function toForm(client: ClientRow): ClientForm {
  return {
    client_type: client.client_type,
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    company_name: client.company_name || '',
    email: client.email || '',
    phone: client.phone || '',
    instagram_handle: client.instagram_handle || '',
    preferred_contact: client.preferred_contact || 'instagram',
    source_id: client.source_id || '',
    notes: client.notes || '',
    marketing_email: client.marketing_email,
    marketing_sms: client.marketing_sms,
    marketing_phone: client.marketing_phone,
    active: client.active
  }
}

export function ClientsPage() {
  const { t, language } = useLanguage()
  const { user } = useAuth()
  const [clients, setClients] = useState<ClientRow[]>([])
  const [sources, setSources] = useState<SimpleSource[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | ClientType>('all')
  const [showInactive, setShowInactive] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [selected, setSelected] = useState<ClientRow | null>(null)
  const [form, setForm] = useState<ClientForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const load = useCallback(async () => {
    if (!supabase) return
    setLoading(true)
    setError(null)
    const [{ data: clientData, error: clientError }, { data: sourceData, error: sourceError }] = await Promise.all([
      supabase.from('client_activity_summary').select('*').order('updated_at', { ascending: false }),
      supabase.from('lead_sources').select('id,code,name_pl,name_ru').eq('active', true).order('name_pl')
    ])
    if (clientError || sourceError) {
      setError(clientError?.message || sourceError?.message || t('errorLoading'))
    } else {
      setClients((clientData || []) as ClientRow[])
      setSources((sourceData || []) as SimpleSource[])
    }
    setLoading(false)
  }, [t])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    const open = () => openCreate()
    if (consumePendingAction('new-client')) openCreate()
    window.addEventListener('sgflow:new-client', open)
    return () => window.removeEventListener('sgflow:new-client', open)
  }, [])

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase()
    return clients.filter((client) => {
      if (!showInactive && !client.active) return false
      if (typeFilter !== 'all' && client.client_type !== typeFilter) return false
      if (!query) return true
      const haystack = [
        clientDisplayName(client),
        client.email,
        client.phone,
        client.instagram_handle,
        client.company_name,
        client.notes
      ].filter(Boolean).join(' ').toLowerCase()
      return haystack.includes(query)
    })
  }, [clients, search, showInactive, typeFilter])

  function openCreate() {
    setSelected(null)
    setForm(emptyForm)
    setNotice(null)
    setModalOpen(true)
  }

  function openEdit(client: ClientRow) {
    setSelected(client)
    setForm(toForm(client))
    setNotice(null)
    setModalOpen(true)
  }

  function update<K extends keyof ClientForm>(key: K, value: ClientForm[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function toggleArchive(client: ClientRow) {
    if (!supabase) return
    setError(null)
    const nextActive = !client.active
    const { error: updateError } = await supabase.from('clients').update({ active: nextActive }).eq('id', client.id)
    if (updateError) {
      setError(updateError.message)
      return
    }
    setNotice(nextActive ? t('clientRestored') : t('clientArchived'))
    await load()
  }

  async function deleteClient(client: ClientRow) {
    if (!supabase || !window.confirm(t('confirmDeleteClient'))) return
    setDeleting(true)
    setError(null)
    const { data, error: deleteError } = await supabase.rpc('delete_client_if_unused', { p_client_id: client.id })
    if (deleteError) {
      setError(deleteError.message)
    } else if (data === 'has_relations') {
      setError(t('clientHasRelations'))
    } else {
      setModalOpen(false)
      setNotice(t('clientDeleted'))
      await load()
    }
    setDeleting(false)
  }

  async function save() {
    if (!supabase || !user) return
    const hasIdentity = form.client_type === 'company'
      ? form.company_name.trim()
      : form.first_name.trim() || form.instagram_handle.trim() || form.email.trim() || form.phone.trim()
    if (!hasIdentity) {
      setError(form.client_type === 'company' ? t('companyName') : t('clientName'))
      return
    }

    setSaving(true)
    setError(null)
    const marketingConsent = form.marketing_email || form.marketing_sms || form.marketing_phone
    const payload = {
      client_type: form.client_type,
      first_name: form.client_type === 'individual' ? form.first_name.trim() || null : null,
      last_name: form.client_type === 'individual' ? form.last_name.trim() || null : null,
      company_name: form.client_type === 'company' ? form.company_name.trim() || null : null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      instagram_handle: form.instagram_handle.trim().replace(/^@/, '') || null,
      preferred_contact: form.preferred_contact || null,
      source_id: form.source_id || null,
      notes: form.notes.trim() || null,
      marketing_email: form.marketing_email,
      marketing_sms: form.marketing_sms,
      marketing_phone: form.marketing_phone,
      marketing_consent_at: marketingConsent ? new Date().toISOString() : null,
      marketing_consent_source: marketingConsent ? 'SG Flow' : null,
      active: form.active
    }

    const result = selected
      ? await supabase.from('clients').update(payload).eq('id', selected.id)
      : await supabase.from('clients').insert({ ...payload, created_by: user.id })

    if (result.error) {
      setError(result.error.message)
    } else {
      setModalOpen(false)
      setNotice(selected ? t('clientUpdated') : t('clientCreated'))
      await load()
    }
    setSaving(false)
  }

  const sourceLabel = (client: ClientRow) => language === 'pl' ? client.source_name_pl : client.source_name_ru

  return (
    <div className="module-stack">
      <section className="module-toolbar panel">
        <div className="toolbar-search">
          <span>⌕</span>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('search')} />
        </div>
        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as 'all' | ClientType)}>
          <option value="all">{t('all')}</option>
          <option value="individual">{t('individual')}</option>
          <option value="company">{t('company')}</option>
        </select>
        <label className="toggle-label"><input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />{t('showInactive')}</label>
        <button className="secondary-button" type="button" onClick={() => void load()}>{t('refresh')}</button>
        <button className="primary-button" type="button" onClick={openCreate}>＋ {t('createClient')}</button>
      </section>

      {notice && <InlineNotice type="success">{notice}</InlineNotice>}
      {error && !modalOpen && <InlineNotice type="error">{error}</InlineNotice>}

      {loading ? <LoadingBlock /> : filtered.length === 0 ? (
        clients.length === 0 ? (
          <EmptyState title={t('clientsEmpty')} description={t('clientsEmptyDescription')} action={<button className="primary-button" onClick={openCreate}>{t('createClient')}</button>} />
        ) : (
          <EmptyState title={t('noResults')} description={t('noResultsDescription')} />
        )
      ) : (
        <section className="data-card panel">
          <div className="table-scroll">
            <table className="data-table">
              <thead><tr><th>{t('client')}</th><th>{t('clientType')}</th><th>{t('firstSource')}</th><th>{t('totalLeads')}</th><th>{t('totalOrders')}</th><th>{t('lifetimeValue')}</th><th>{t('actions')}</th></tr></thead>
              <tbody>
                {filtered.map((client) => (
                  <tr key={client.id} className={!client.active ? 'row-muted' : ''}>
                    <td>
                      <button className="record-title" type="button" onClick={() => openEdit(client)}>{clientDisplayName(client)}</button>
                      <div className="record-subtitle">{client.email || client.phone || (client.instagram_handle ? `@${client.instagram_handle}` : '—')}</div>
                    </td>
                    <td><StatusBadge tone={client.client_type === 'company' ? 'accent' : 'neutral'}>{client.client_type === 'company' ? t('company') : t('individual')}</StatusBadge></td>
                    <td>{sourceLabel(client) || '—'}</td>
                    <td>{client.lead_count || 0}</td>
                    <td>{client.order_count || 0}</td>
                    <td>{formatMoney(Number(client.lifetime_value_gross || 0), language)}</td>
                    <td><div className="row-actions"><button className="table-action" type="button" onClick={() => openEdit(client)}>{t('edit')}</button><button className="table-action" type="button" onClick={() => void toggleArchive(client)}>{client.active ? t('archive') : t('restore')}</button>{Number(client.lead_count || 0) === 0 && Number(client.order_count || 0) === 0 && <button className="table-action danger-text" type="button" onClick={() => void deleteClient(client)}>{t('deletePermanently')}</button>}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Modal
        open={modalOpen}
        title={selected ? t('editClient') : t('createClient')}
        subtitle={selected ? `${clientDisplayName(selected)} · ${formatDate(selected.updated_at, language, true)}` : t('requiredFields')}
        onClose={() => setModalOpen(false)}
        wide
        footer={<>{selected && Number(selected.lead_count || 0) === 0 && Number(selected.order_count || 0) === 0 && <button className="secondary-button danger-button" disabled={deleting} onClick={() => void deleteClient(selected)}>{t('deletePermanently')}</button>}<button className="secondary-button" onClick={() => setModalOpen(false)}>{t('cancel')}</button><button className="primary-button" disabled={saving || deleting} onClick={() => void save()}>{saving ? t('saving') : t('save')}</button></>}
      >
        {error && <InlineNotice type="error">{error}</InlineNotice>}
        <div className="form-grid">
          <label className="field"><span>{t('clientType')} *</span><select value={form.client_type} onChange={(event) => update('client_type', event.target.value as ClientType)}><option value="individual">{t('individual')}</option><option value="company">{t('company')}</option></select></label>
          {form.client_type === 'company' ? (
            <label className="field field-span-2"><span>{t('companyName')} *</span><input value={form.company_name} onChange={(event) => update('company_name', event.target.value)} /></label>
          ) : (
            <><label className="field"><span>{t('firstName')}</span><input value={form.first_name} onChange={(event) => update('first_name', event.target.value)} /></label><label className="field"><span>{t('lastName')}</span><input value={form.last_name} onChange={(event) => update('last_name', event.target.value)} /></label></>
          )}
          <label className="field"><span>{t('email')}</span><input type="email" value={form.email} onChange={(event) => update('email', event.target.value)} /></label>
          <label className="field"><span>{t('phone')}</span><input value={form.phone} onChange={(event) => update('phone', event.target.value)} /></label>
          <label className="field"><span>{t('instagram')}</span><input value={form.instagram_handle} onChange={(event) => update('instagram_handle', event.target.value)} placeholder="sistersgarden" /></label>
          <label className="field"><span>{t('preferredContact')}</span><select value={form.preferred_contact} onChange={(event) => update('preferred_contact', event.target.value)}><option value="instagram">Instagram</option><option value="phone">{t('phone')}</option><option value="email">Email</option><option value="sms">SMS</option></select></label>
          <label className="field"><span>{t('firstSource')}</span><select value={form.source_id} onChange={(event) => update('source_id', event.target.value)}><option value="">—</option>{sources.map((source) => <option key={source.id} value={source.id}>{language === 'pl' ? source.name_pl : source.name_ru}</option>)}</select></label>
          <label className="field"><span>{t('status')}</span><select value={form.active ? 'active' : 'archived'} onChange={(event) => update('active', event.target.value === 'active')}><option value="active">{t('active')}</option><option value="archived">{t('archived')}</option></select></label>
          <label className="field field-span-3"><span>{t('notes')}</span><textarea rows={4} value={form.notes} onChange={(event) => update('notes', event.target.value)} /></label>
        </div>
        <div className="form-section">
          <h3>{t('marketingConsent')}</h3>
          <div className="checkbox-grid">
            <label><input type="checkbox" checked={form.marketing_email} onChange={(event) => update('marketing_email', event.target.checked)} />{t('marketingEmail')}</label>
            <label><input type="checkbox" checked={form.marketing_sms} onChange={(event) => update('marketing_sms', event.target.checked)} />{t('marketingSms')}</label>
            <label><input type="checkbox" checked={form.marketing_phone} onChange={(event) => update('marketing_phone', event.target.checked)} />{t('marketingPhone')}</label>
          </div>
        </div>
      </Modal>
    </div>
  )
}
